/**
 * Computes a spatially-continuous traversal order for 2D points using
 * a nearest-neighbor heuristic. The resulting sequence visits nearby
 * points consecutively so an animation "shimmer" sweeps smoothly
 * across the map instead of jumping around.
 */

export interface ShimmerPoint {
    id: string;
    x: number;
    y: number;
}

/**
 * Given a set of 2D points, return their IDs in a spatially-continuous
 * order (greedy nearest-neighbor starting from the point closest to
 * the top-left corner).
 */
export function computeShimmerOrder(points: ShimmerPoint[]): string[] {
    if (points.length === 0) return [];
    if (points.length === 1) return [points[0].id];

    const visited: boolean[] = new Array(points.length).fill(false);

    // Start from the point nearest to the top-left (min x + min y)
    let currentIdx = 0;
    let bestStart = Infinity;
    for (let i = 0; i < points.length; i++) {
        const score = points[i].x + points[i].y;
        if (score < bestStart) {
            bestStart = score;
            currentIdx = i;
        }
    }

    const order: string[] = [points[currentIdx].id];
    visited[currentIdx] = true;
    let remainingCount = points.length - 1;

    while (remainingCount > 0) {
        const cur = points[currentIdx];
        let nearestIdx = -1;
        let nearestDist = Infinity;

        for (let i = 0; i < points.length; i++) {
            if (visited[i]) continue;
            const dx = points[i].x - cur.x;
            const dy = points[i].y - cur.y;
            const dist = dx * dx + dy * dy;
            if (dist < nearestDist) {
                nearestDist = dist;
                nearestIdx = i;
            }
        }

        order.push(points[nearestIdx].id);
        visited[nearestIdx] = true;
        remainingCount--;
        currentIdx = nearestIdx;
    }

    return order;
}
